import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { styles, StatusPill, fmtMoney, fmtMoneyK, fmtDate, fmtDateTime } from '../components/UI';
import CustomerInvoices from '../components/CustomerInvoices';

const INLINE_STATUSES = ['In the Hunt', 'Bidding', 'Mobilization Standby', 'Won', 'Active', 'Complete', 'Closed'];

export default function AccountDetail() {
  const { name: nameParam } = useParams();
  const name = decodeURIComponent(nameParam);
  const [data, setData] = useState(null);
  const [rentalman, setRentalman] = useState([]);
  const [error, setError] = useState('');

  const [saveError, setSaveError] = useState('');
  // Per-row rev, bumped after that row's inline save so ITS uncontrolled
  // inputs re-mount on the stored value (including after a rejected edit).
  // Per row, so a save finishing on one row never wipes typing on another.
  const [rowRev, setRowRev] = useState({});
  const remountRow = (id) => setRowRev(m => ({ ...m, [id]: (m[id] || 0) + 1 }));
  const reloadAccount = () => api.getAccount(name).then(setData).catch(e => setError(e.message));

  // Inline edits save one field through the same Tracker PUT (server rules,
  // History, WON alert and FS push all apply), then reload the account so the
  // KPI cards follow. A rejected value (e.g. a two-digit year) shows the
  // server's message and the reload puts the stored value back.
  const saveField = async (p, field, value) => {
    setSaveError('');
    try { await api.updateProject(p.id, { [field]: value }); }
    catch (e) { setSaveError(`${p.site}: ${e.message}`); }
    await reloadAccount();
    remountRow(p.id);
  };

  // Moving a deal into Won / Active / Complete needs the customer PO (server
  // rule, utils/poOnWon.js). Ask for it here and send both in ONE save so the
  // WON alert and the RentalMan match see the PO. TBD is accepted and nagged.
  // Revenue Type is required the same way (utils/revenueType.js): a legacy
  // row without one is asked for it here, in the same save.
  const saveStatus = async (p, status) => {
    const awarded = ['Won', 'Active', 'Complete'];
    const entering = awarded.includes(status) && !awarded.includes(p.status);
    const needPo = entering && !String(p.po_number || '').trim();
    const needType = entering && !p.revenue_type;
    if (needPo || needType) {
      const extra = {};
      if (needType) {
        const t = window.prompt(`Revenue Type for ${p.site}?\nEnter TA, Branch or Rescue. TA deals get their TA Job # automatically.`, '');
        const match = ['TA', 'Branch', 'Rescue'].find(v => v.toLowerCase() === String(t || '').trim().toLowerCase());
        if (!match) {
          if (t !== null) setSaveError(`${p.site}: Revenue Type must be TA, Branch or Rescue.`);
          remountRow(p.id); return;
        }
        extra.revenue_type = match;
      }
      if (needPo) {
        const po = window.prompt(`Customer PO # for ${p.site}?\nIf the PO has not arrived yet, enter TBD.`, '');
        if (po === null || !po.trim()) { remountRow(p.id); return; }
        extra.po_number = po.trim();
      }
      setSaveError('');
      try { await api.updateProject(p.id, { status, ...extra }); }
      catch (e) { setSaveError(`${p.site}: ${e.message}`); }
      await reloadAccount();
      remountRow(p.id);
      return;
    }
    saveField(p, 'status', status);
  };

  useEffect(() => {
    reloadAccount();
    // Best-effort match into the Rentalman master. May return 0, 1, or
    // many (BBB Tank - Baytown vs BBB Tank - Tulsa for example).
    api.customersByName(name).then(setRentalman).catch(() => setRentalman([]));
  }, [name]);

  if (error) return <div style={styles.errorBox}>{error}</div>;
  if (!data) return <div>Loading...</div>;

  // Won $ (docs/SPEC-rm-invoiced-actuals.md section 8): Actual $ is now the
  // RentalMan-invoiced total, which grows invoice by invoice while a job runs.
  // So the Actual counts only once the job is Complete; before that the
  // estimate does, and a half-billed Active job no longer shrinks the account.
  // Summed over the same Won/Active/Complete rows the server's stat used.
  const wonValue = (data.projects || [])
    .filter(p => ['Won', 'Active', 'Complete'].includes(p.status))
    .reduce((s, p) => s + Number(
      (p.status === 'Complete' && p.actual_value != null) ? p.actual_value : (p.estimated_value || 0)
    ), 0);

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Link to="/accounts" style={{ color: '#6B7280', fontSize: 13, textDecoration: 'none' }}>← All accounts</Link>
        <h1 style={{ ...styles.h1, marginTop: 4, marginBottom: 4 }}>{name}</h1>
        <div style={{ fontSize: 13, color: '#6B7280' }}>
          Last contact {fmtDateTime(data.stats.last_contact_at) || 'never'}
        </div>
      </div>

      {/* KPI cards */}
      <div style={styles.kpiGrid}>
        <Kpi label="Pipeline" value={fmtMoneyK(data.stats.pipeline_value)} sub="open bids" />
        <Kpi label="Won" value={fmtMoneyK(wonValue)} sub="booked" />
        <Kpi label="Projects" value={data.stats.project_count} sub="all-time" />
        <Kpi label="Call Log Entries" value={data.stats.entry_count} sub="all-time" />
      </div>

      {/* Rentalman master record(s). Multiple is normal -- e.g. BBB Tank
          shows a Baytown row and a Tulsa row. Surfaces the Rentalman
          status so the rep knows if Rentalman thinks this customer is
          Active / Suspended / Deleted, plus which Sales ID is on file. */}
      {rentalman.length > 0 && (
        <div style={styles.card}>
          <h2 style={styles.h2}>
            Rentalman Master <span style={{ color: '#6B7280', fontWeight: 400 }}>· {rentalman.length} match{rentalman.length === 1 ? '' : 'es'}</span>
          </h2>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Acct #</th>
                <th style={styles.th}>Loc</th>
                <th style={styles.th}>Sales Rep (Rentalman)</th>
                <th style={styles.th}>Address</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Email</th>
              </tr>
            </thead>
            <tbody>
              {rentalman.map(c => (
                <tr key={c.id}>
                  <td style={styles.td}>{c.status || ''}</td>
                  <td style={styles.td}>{c.account_no}</td>
                  <td style={styles.td}>{c.location_code || ''}</td>
                  <td style={styles.td}>
                    {c.assigned_to_name && (
                      <div>
                        <strong>{c.assigned_to_name}</strong>
                        <div style={{ fontSize: 10, color: '#3730A3', fontWeight: 700, letterSpacing: 0.4 }}>
                          ASSIGNED TO CHASE
                        </div>
                      </div>
                    )}
                    {!c.assigned_to_name && (c.rentalman_owner_name || (c.sales_id ? `Sales ID ${c.sales_id}` : <span style={{ color: '#9CA3AF' }}>Unassigned</span>))}
                    {c.assigned_to_name && c.rentalman_owner_name && c.rentalman_owner_name !== c.assigned_to_name && (
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                        Rentalman: {c.rentalman_owner_name}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    {[c.address1, c.address2, c.state, c.zip_code].filter(Boolean).join(', ')}
                  </td>
                  <td style={styles.td}>{c.phone || ''}</td>
                  <td style={{ ...styles.td, fontSize: 12 }}>{c.email || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Every RentalMan invoice on the matched account(s). */}
      <CustomerInvoices accounts={rentalman} />

      {/* Contacts */}
      {data.contacts.length > 0 && (
        <div style={styles.card}>
          <h2 style={styles.h2}>Contacts <span style={{ color: '#6B7280', fontWeight: 400 }}>· {data.contacts.length}</span></h2>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Last Mentioned</th>
              </tr>
            </thead>
            <tbody>
              {data.contacts.map(c => (
                <tr key={c.name}>
                  <td style={styles.td}><strong>{c.name}</strong></td>
                  <td style={styles.td}>{c.title || ''}</td>
                  <td style={styles.td}>{fmtDateTime(c.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Projects */}
      {data.projects.length > 0 && (
        <div style={styles.card}>
          <h2 style={styles.h2}>Projects <span style={{ color: '#6B7280', fontWeight: 400 }}>· {data.projects.length}</span></h2>
          {saveError && <div style={styles.errorBox}>{saveError}</div>}
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>Equipment</th>
                <th style={styles.th}>Est. $</th>
                <th style={styles.th}>%W</th>
                <th style={styles.th}>Acct Mgr</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {data.projects.map(p => (
                <tr key={p.id}>
                  <td style={styles.td}>
                    <input type="date" key={`d${p.id}-${rowRev[p.id] || 0}`} style={{ ...styles.input, width: 140 }}
                      defaultValue={p.date_planned ? String(p.date_planned).slice(0, 10) : ''}
                      onBlur={e => {
                        // A half-cleared date reads as '' but is not a clear:
                        // put the stored date back instead of saving NULL.
                        if (e.target.validity.badInput) { remountRow(p.id); return; }
                        const v = e.target.value || null;
                        const was = p.date_planned ? String(p.date_planned).slice(0, 10) : null;
                        if (v !== was) saveField(p, 'date_planned', v);
                      }} />
                  </td>
                  <td style={styles.td}><strong>{p.site}</strong></td>
                  <td style={{ ...styles.td, maxWidth: 320, fontSize: 12 }}>{p.equipment_type || ''}</td>
                  <td style={styles.td}>{fmtMoney(p.estimated_value)}</td>
                  <td style={styles.td}>
                    <input type="number" min="0" max="100" step="1" key={`w${p.id}-${rowRev[p.id] || 0}`} style={{ ...styles.input, width: 64 }}
                      defaultValue={p.win_pct ?? ''}
                      onBlur={e => {
                        // Win % is required, 0-100 whole number (the server
                        // 400s anything else): never send a blank or bad
                        // value, put the stored one back instead.
                        const raw = e.target.value.trim();
                        const v = Number(raw);
                        if (raw === '' || !Number.isInteger(v) || v < 0 || v > 100) {
                          setSaveError(`${p.site}: Win % must be a whole number from 0 to 100.`);
                          remountRow(p.id);
                          return;
                        }
                        if (v !== (p.win_pct ?? null)) saveField(p, 'win_pct', v);
                      }} />
                  </td>
                  <td style={styles.td}>{p.account_owner_name || ''}</td>
                  <td style={styles.td}>
                    {/* Lost needs a reason, so it is set from the full edit form. */}
                    <select style={{ ...styles.input, width: 120 }} value={p.status}
                      onChange={e => saveStatus(p, e.target.value)}>
                      {INLINE_STATUSES.concat(p.status === 'Lost' ? ['Lost'] : []).map(s =>
                        <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={styles.td}>
                    <Link to={`/projects?year=&edit=${p.id}`} style={{ fontSize: 12 }}>Edit</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Call Log Entries */}
      {data.entries.length > 0 && (
        <div style={styles.card}>
          <h2 style={styles.h2}>Call Log <span style={{ color: '#6B7280', fontWeight: 400 }}>· {data.entries.length}</span></h2>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Week</th>
                <th style={styles.th}>Account Mgr</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Notes</th>
                <th style={styles.th}>Follow-up</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map(e => (
                <tr key={e.id} style={e.supervisor_flag ? { background: '#FEF3C7' } : {}}>
                  <td style={styles.td}>{fmtDate(e.week_of)}</td>
                  <td style={styles.td}>{e.rep_name}</td>
                  <td style={styles.td}>
                    {e.contact_name}
                    {e.contact_title && <div style={{ color: '#6B7280', fontSize: 12 }}>{e.contact_title}</div>}
                  </td>
                  <td style={{ ...styles.td, maxWidth: 420, whiteSpace: 'pre-wrap' }}>{e.notes}</td>
                  <td style={styles.td}>
                    {fmtDate(e.follow_up_date)}
                    {e.follow_up_type && <div style={{ color: '#6B7280', fontSize: 12 }}>{e.follow_up_type}</div>}
                  </td>
                  <td style={styles.td}><StatusPill status={e.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.entries.length === 0 && data.projects.length === 0 && (
        <div style={styles.card}>
          <p style={{ color: '#6B7280', textAlign: 'center', margin: 0 }}>No activity yet for this account.</p>
        </div>
      )}
    </>
  );
}

const Kpi = ({ label, value, sub }) => (
  <div style={styles.kpiCard}>
    <div style={styles.kpiLabel}>{label}</div>
    <div style={styles.kpiValue}>{value}</div>
    {sub && <div style={styles.kpiSubtext}>{sub}</div>}
  </div>
);
